/* eslint-disable jsx-a11y/control-has-associated-label, react/no-danger, react/jsx-props-no-spreading */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import * as AppPropTypes from '../../lib/PropTypes';
// import useWebp from '../../hooks/useWebp';
import useKeyboardKeys, { KEYS } from '../../hooks/useKeyboardKeys';
import useSwipe from '../../hooks/useSwipe';
import Html from './Html';
import Image from './Image';
import Arrow from '../buttons/Arrow';
import { capitalizeFirstLetter } from '../../lib/utils';

import styles from '../../../styles/partials/gallery-viewer.module.scss';

const propTypes = {
    images: AppPropTypes.images.isRequired,
    image: AppPropTypes.image,
    onClickClose: PropTypes.func.isRequired,
    timeout: PropTypes.number,
    transition: PropTypes.string,
    fit: PropTypes.string,
    filter: PropTypes.string,
    withoutArrows: PropTypes.bool,
    className: PropTypes.string,
    slidesClassName: PropTypes.string,
    imageClassName: PropTypes.string,
    imageStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

const defaultProps = {
    image: null,
    timeout: null,
    transition: null,
    fit: 'cover',
    filter: null,
    withoutArrows: false,
    className: null,
    slidesClassName: null,
    imageClassName: null,
    imageStyle: null,
};

const GalleryViewer = ({
    images,
    image: providedImage,
    onClickClose,
    timeout,
    transition,
    fit,
    filter,
    withoutArrows,
    className,
    slidesClassName,
    imageClassName,
    imageStyle,
}) => {
    const [currentImage, setCurrentImage] = useState(providedImage);
    const currentIndex = currentImage !== null ? images.findIndex((it) => it === currentImage) : 0;

    const nextSlide = useCallback(() => {
        const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
        setCurrentImage(images[nextIndex]);
    }, [images, currentIndex, setCurrentImage]);

    const previousSlide = useCallback(() => {
        const nextIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
        setCurrentImage(images[nextIndex]);
    }, [images, currentIndex, setCurrentImage]);

    useEffect(() => {
        if (providedImage !== currentImage) {
            setCurrentImage(providedImage);
        }
    }, [providedImage]);

    useKeyboardKeys({
        [KEYS.ARROW_LEFT]: previousSlide,
        [KEYS.ARROW_RIGHT]: nextSlide,
        [KEYS.ESCAPE]: onClickClose,
    });

    const bindSwipe = useSwipe({
        onSwipeRight: previousSlide,
        onSwipeLeft: nextSlide,
    });

    const offset = useMemo(() => (Math.random() * timeout) / 2, [timeout]);

    useEffect(() => {
        let id = null;
        if (timeout) {
            id = setInterval(() => {
                nextSlide();
            }, timeout + offset);
        }
        return () => {
            clearInterval(id);
        };
    }, [timeout, offset, nextSlide]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles[`${filter}`]]: filter !== null,
                    [styles[`transition${capitalizeFirstLetter(transition)}`]]: transition !== null,
                },
            ])}
        >
            <div className={styles.slideshow} {...bindSwipe()}>
                {!withoutArrows ? (
                    <Arrow
                        className={classNames([styles.arrow, styles.arrowLeft])}
                        onClick={previousSlide}
                    />
                ) : null}
                <div className={styles.frame}>
                    <div
                        className={classNames([
                            styles.slides,
                            {
                                [slidesClassName]: slidesClassName !== null,
                            },
                        ])}
                    >
                        {images.map((image, index) => {
                            const { alt = null, title = null, description = null } = image || {};

                            return (
                                <div
                                    key={`slide-${image.id}-${index + 1}`}
                                    className={classNames([
                                        styles.slide,
                                        {
                                            [styles.previous]: index < currentIndex,
                                            [styles.current]: index === currentIndex,
                                            [styles.next]: index > currentIndex,
                                        },
                                    ])}
                                >
                                    <Image
                                        className={classNames([
                                            styles.image,
                                            {
                                                [imageClassName]: imageClassName !== null,
                                            },
                                        ])}
                                        media={image}
                                        size="gallery"
                                        fit={fit}
                                        alt={alt}
                                        style={imageStyle}
                                    />
                                    {title !== null || description !== null ? (
                                        <div className={styles.caption}>
                                            {title !== null ? (
                                                <div
                                                    className={styles.title}
                                                    dangerouslySetInnerHTML={{
                                                        __html: image.title,
                                                    }}
                                                />
                                            ) : null}
                                            {description !== null ? (
                                                <Html
                                                    className={styles.description}
                                                    body={image.description}
                                                />
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
                {!withoutArrows ? (
                    <Arrow
                        className={classNames([styles.arrow, styles.arrowRight])}
                        onClick={nextSlide}
                    />
                ) : null}
            </div>
        </div>
    );
};

GalleryViewer.propTypes = propTypes;
GalleryViewer.defaultProps = defaultProps;

export default GalleryViewer;
